import React from 'react';
import styled from 'styled-components';

type StatAccent = 'ink' | 'green' | 'danger' | 'warning' | 'muted';

const ACCENT_DOT: Record<StatAccent, string> = {
  ink: 'var(--brand-ink)',
  green: 'var(--brand-green)',
  danger: 'var(--danger)',
  warning: 'var(--warning)',
  muted: 'var(--muted-3)',
};

const ACCENT_BORDER: Record<StatAccent, string> = {
  ink: 'var(--brand-ink)',
  green: 'var(--brand-green)',
  danger: 'var(--danger)',
  warning: 'var(--warning)',
  muted: 'transparent',
};

const Card = styled.div<{ $accent?: StatAccent; $showBorder?: boolean }>`
  background: var(--surface);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-1);
  padding: var(--s-5);
  padding-top: calc(var(--s-5) + 3px);
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  position: relative;
  overflow: hidden;

  ${({ $showBorder, $accent }) => $showBorder && $accent && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: ${ACCENT_BORDER[$accent]};
    }
  `}
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Dot = styled.span<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const Label = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
`;

const Value = styled.div`
  font-size: var(--t-3xl);
  font-weight: 700;
  color: var(--ink);
  line-height: 1;
  letter-spacing: var(--tracking-tight);
  font-variant-numeric: tabular-nums;
  margin-top: 4px;
`;

const Sub = styled.div`
  font-size: var(--t-xs, 0.75rem);
  color: var(--muted);
  line-height: 1.3;
`;

const TrendRow = styled.div<{ $up: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  font-size: var(--t-xs, 0.75rem);
  color: ${({ $up }) => ($up ? 'var(--success-ink)' : 'var(--danger-ink)')};
  font-weight: 600;
`;

const TrendArrow = styled.span<{ $up: boolean }>`
  display: flex;
  transform: ${({ $up }) => ($up ? 'none' : 'rotate(180deg)')};
  svg {
    width: 12px;
    height: 12px;
  }
`;

interface TrendProps {
  dir: 'up' | 'down';
  label: string;
}

interface ScStatProps {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  accent?: StatAccent;
  trend?: TrendProps;
  isLoading?: boolean;
  showBorder?: boolean;
}

const ArrowUpSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5"/>
    <polyline points="5 12 12 5 19 12"/>
  </svg>
);

const ScStat: React.FC<ScStatProps> = ({
  label,
  value,
  sub,
  accent = 'ink',
  trend,
  isLoading,
  showBorder,
}) => (
  <Card $accent={accent} $showBorder={showBorder}>
    <LabelRow>
      <Dot $color={ACCENT_DOT[accent]} />
      <Label>{label}</Label>
    </LabelRow>
    <Value>{isLoading ? '—' : value}</Value>
    {sub && <Sub>{sub}</Sub>}
    {trend && !isLoading && (
      <TrendRow $up={trend.dir === 'up'}>
        <TrendArrow $up={trend.dir === 'up'}>
          <ArrowUpSvg />
        </TrendArrow>
        <span>{trend.label}</span>
      </TrendRow>
    )}
  </Card>
);

export default ScStat;
